import React from 'react';
import { graphql, type PageProps, type HeadFC, type HeadProps } from 'gatsby';
import { navigate } from 'gatsby';
import Layout from "components/layout";
import Seo, { SeoProps } from "components/seo";
import type { GatsbyCreatePageContext } from "types/gatsby-create-page-context";
import { SignatureEventClass } from 'types/signature-event-class';
import { useLocation } from '@reach/router';

// import components
import DebugPageData from 'components/partials/debug-page-data';
import { ContentBlocks } from 'types/content-blocks';
import PageHeader from 'components/partials/page_headers/page-header';

// import helpers
import InPageAlertComponentBuilder from 'helpers/builders/in-page-alert-builder';
import SponsorBarComponentBuilder from 'helpers/builders/sponsor-bar-builder';
import CountdownSlimBuilder from 'helpers/builders/countdown-slim-builder';
import HeroFeatureComponentBuilder from 'helpers/builders/hero-feature-builder';
import ContentBlocksBuilder from 'helpers/builders/content-blocks-builder';
import DealModalBuilder from 'helpers/builders/deal-modal-builder';
import { getLinkById } from 'hooks/use-generate-site-link';

const SignatureEventLandingPageTemplate: React.FC<PageProps<Queries.SignatureEventLandingPageQuery, GatsbyCreatePageContext>> = ({ data, pageContext }) => {

    if (data.datoCmsPage?.pageTemplate?.__typename !== 'DatoCmsSignatureEventLandingPageTemplate') {
        // wrong template type, get out of here!
        // TODO: Maybe add an error message or logging?
        navigate('/404');
        return;
    }
    const inPageAlertData = data.datoCmsPage?.pageTemplate?.inPageAlert as Queries.InPageAlertBlockFragment | undefined;

    let AlertBar = InPageAlertComponentBuilder({ inPageAlertData });

    const sponsorBarData = data.datoCmsPage?.pageTemplate?.sponsorBar as Queries.SponsorBarBlockFragment | undefined;

    let SponsorBar = SponsorBarComponentBuilder({ sponsorBarData });

    const countdownData = data.datoCmsPage?.pageTemplate?.countdown as Queries.CountdownBlockFragment | undefined;

    // Build the CountdownSlim component
    const CountdownSlim = CountdownSlimBuilder({ countdownData });

    const heroData = data.datoCmsPage?.pageTemplate?.hero as Queries.HeroImageBlockFragment | undefined;

    let HeroFeature = HeroFeatureComponentBuilder({ heroData });

    if (!HeroFeature) {
        // NOTE: Hero is required but adding this check to prevent possible issues
        HeroFeature = <PageHeader title={data.datoCmsPage?.title ?? 'Missing Title'} />;
    }

    // Build the PageTemplateContent component
    let ContentBlocks = ContentBlocksBuilder({ contentBlocks: data.datoCmsPage.pageTemplate.content as ContentBlocks });

    const layoutThemeClass = getThemeClassFromColorScheme(data.datoCmsPage.pageTemplate.colorScheme ?? '');

    return (
        <Layout navSection={pageContext.navSection} breadcrumbs={pageContext.breadcrumbs} currentPageId={pageContext.id} signatureTheme={layoutThemeClass}>
            <DebugPageData
                title={data.datoCmsPage?.title}
                templateName="Signature Event Landing Page"
                pageTemplate={data.datoCmsPage?.pageTemplate?.__typename}
                jsonData={JSON.stringify(data, null, 2)} />
            {AlertBar}
            {SponsorBar}
            {CountdownSlim}
            {HeroFeature}
            {ContentBlocks}
            {data.datoCmsPage?.dealModal && <DealModalBuilder dealModalData={data.datoCmsPage?.dealModal} />}
        </Layout>
    )
};

export default SignatureEventLandingPageTemplate;

export const Head: HeadFC<Queries.SignatureEventLandingPageQuery, GatsbyCreatePageContext> = (props: HeadProps<Queries.SignatureEventLandingPageQuery, GatsbyCreatePageContext>) => {
    
    const canonicalLink = getCanonicalLinkIfApplicable(props.data);

    return (
        <Seo title={props.data.datoCmsPage?.title ?? 'Unknown Page'} pageSeoMetaTags={props.data.datoCmsPage?.seoMetaTags?.tags}>
            {canonicalLink &&
                <link rel="canonical" href={canonicalLink} />
            }
        </Seo>
    );
};

export const query = graphql`
    query SignatureEventLandingPage($id: String!) {
            datoCmsPage(id: { eq: $id }) {
                ...PageTemplateFields
            }
    }
`;

const getCanonicalLinkIfApplicable = (data: Queries.SignatureEventLandingPageQuery) => {
    const signatureEventId = data.datoCmsPage?.pageTemplate?.__typename === 'DatoCmsSignatureEventLandingPageTemplate'
        ? data.datoCmsPage?.pageTemplate.signatureEvent?.id
        : null;
    let path = getLinkById(data.datoCmsPage?.id ?? null, signatureEventId);
    if (path !== null) {
        const location = useLocation();
        path = `${location?.origin ?? ''}${path}`;
    }

    return path;
};

const getThemeClassFromColorScheme = (colorScheme: string): SignatureEventClass => {
    let themeClass: SignatureEventClass = 'sig-main';

    switch (colorScheme) {
        case 'spring-fair':
            themeClass = 'sig-wsf-spring';
            break;
        case 'taste-nw':
            themeClass = 'sig-taste';
            break;
        case 'state-fair':
            themeClass = 'sig-wsf';
            break;
        case 'oktoberfest':
            themeClass = 'sig-oktober';
            break;
        case 'holiday-magic':
            themeClass = 'sig-holiday-magic';
            break;
        case 'foundation':
            themeClass = 'sig-foundation';
            break;
    }

    return themeClass;
};